import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, TextField, Stack, Select, MenuItem, Alert } from '@mui/material';
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadAssets, createAssetRecord, updateAssetRecord, deleteAssetRecord } from './assetsSlice';
import { tokens } from '../../theme/theme';
import useCurrentPermissions from '../../hooks/useCurrentPermissions';

const CRIT_COLOR = { low: tokens.severity.low, medium: tokens.severity.medium, high: tokens.severity.high, critical: tokens.severity.critical };
const STATUS_COLOR = { active: tokens.severity.low, decommissioned: tokens.text.muted, under_maintenance: tokens.severity.medium };
const ASSET_TYPES = ['SERVER', 'WORKSTATION', 'NETWORK_DEVICE', 'CLOUD_RESOURCE', 'APPLICATION', 'MOBILE_DEVICE'];

export default function AssetsPage() {
  const dispatch = useDispatch();
  const { items, mutationStatus, error } = useSelector((s) => s.businessAssets);
  const { can, loaded } = useCurrentPermissions();
  const canWrite = can('asset:write');
  const [name, setName] = useState('');
  const [type, setType] = useState('SERVER');

  useEffect(() => { dispatch(loadAssets()); }, [dispatch]);

  const critical = items.filter((a) => a.criticality === 'critical').length;
  const active = items.filter((a) => a.status === 'active').length;

  const handleCreate = () => {
    if (!canWrite || !name.trim()) return;
    dispatch(createAssetRecord({ name: name.trim(), type, criticality: 'MEDIUM' })).unwrap()
      .then(() => setName(''))
      .catch(() => {});
  };

  return (
    <Box>
      <PageHeader
        title="Asset Management"
        subtitle="Business-context CMDB — ownership, criticality, location. Complements Network Discovery's technical asset feed rather than duplicating it."
      />
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Assets Tracked" value={items.length} icon={InventoryRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Active" value={active} accent={tokens.severity.low} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Critical" value={critical} accent={tokens.severity.critical} /></Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loaded && !canWrite && <Alert severity="info" sx={{ mb: 2 }}>Your role has read-only asset access. Ask an administrator for asset:write permission to add, change or delete assets.</Alert>}
      <Paper sx={{ p: 2, mb: 2.5 }}>
        <Stack direction="row" spacing={1.5}>
          <TextField disabled={!canWrite} size="small" fullWidth placeholder="Asset name" value={name} onChange={(e) => setName(e.target.value)} />
          <Select disabled={!canWrite} size="small" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 180 }}>
            {ASSET_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
          </Select>
          <Button variant="contained" disabled={!canWrite || mutationStatus === 'loading'} onClick={handleCreate}>Add Asset</Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Type', 'Criticality', 'Status', 'IP Address', 'Owner', 'Location', 'Actions'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell sx={{ fontSize: '0.8rem', color: tokens.text.primary }}>{a.name}</TableCell>
                <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.muted }}>{a.type.replace('_', ' ')}</TableCell>
                <TableCell><Chip label={a.criticality} size="small" sx={{ color: CRIT_COLOR[a.criticality], border: `1px solid ${CRIT_COLOR[a.criticality]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell><Chip label={a.status.replace('_', ' ')} size="small" sx={{ color: STATUS_COLOR[a.status], border: `1px solid ${STATUS_COLOR[a.status]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.76rem' }}>{a.ipAddress || '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{a.owner}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.muted }}>{a.location}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" disabled={!canWrite || mutationStatus === 'loading'} onClick={() => dispatch(updateAssetRecord({ id: a.id, changes: { status: a.status === 'under_maintenance' ? 'ACTIVE' : 'UNDER_MAINTENANCE' } }))}>
                      {a.status === 'under_maintenance' ? 'Activate' : 'Maintenance'}
                    </Button>
                    <Button size="small" color="error" disabled={!canWrite || mutationStatus === 'loading'} onClick={() => { if (window.confirm(`Delete asset “${a.name}”?`)) dispatch(deleteAssetRecord(a.id)); }}>
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
